import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { log } from './production-logger';


type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: any;
  stack?: string;
}

class LoggingService {
  private static instance: LoggingService;
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private storageKey = '@app_logs';
  private isProduction = process.env.NODE_ENV === 'production';
  private logsLoaded = false;

  private constructor() {
    // Defer loading logs to prevent circular dependencies
    // Logs will be loaded on first use
  }

  static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  /**
   * Load logs from storage
   */
  private async loadLogs() {
    if (this.logsLoaded) return;
    
    try {
      const storedLogs = await AsyncStorage.getItem(this.storageKey);
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs);
      }
      this.logsLoaded = true;
    } catch (error) {
      log.error('Failed to load logs:', error);
      this.logsLoaded = true; // Mark as loaded even on error
    }
  }
  
  /**
   * Ensure logs are loaded before use
   */
  private async ensureLogsLoaded() {
    if (!this.logsLoaded) {
      await this.loadLogs();
    }
  }

  /**
   * Save logs to storage
   */
  private async saveLogs() {
    try {
      // Keep only the most recent logs
      if (this.logs.length > this.maxLogs) {
        this.logs = this.logs.slice(-this.maxLogs);
      }
      await AsyncStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    } catch (error) {
      log.error('Failed to save logs:', error);
    }
  }

  /**
   * Add a log entry
   */
  private addLog(level: LogLevel, message: string, context?: any, stack?: string) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      stack,
    };

    this.logs.push(entry);
    
    // In development, always log to console
    if (!this.isProduction) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, context || '');
    }

    // Save logs asynchronously
    this.saveLogs();
  }

  /**
   * Debug log
   */
  debug(message: string, context?: any) {
    if (!this.isProduction) {
      this.addLog('debug', message, context);
    }
  }

  /**
   * Info log
   */
  info(message: string, context?: any) {
    this.addLog('info', message, context);
  }

  /**
   * Warning log
   */
  warn(message: string, context?: any) {
    this.addLog('warn', message, context);
  }

  /**
   * Error log
   */
  error(message: string, error?: Error | any, context?: any) {
    const errorContext = {
      ...context,
      errorMessage: error?.message,
      errorName: error?.name,
      errorCode: error?.code,
    };
    
    this.addLog('error', message, errorContext, error?.stack);
  }

  /**
   * Log error (alias for error method for compatibility)
   */
  logError(message: string, error?: Error | any, context?: any) {
    this.error(message, error, context);
  }

  /**
   * Clear all logs
   */
  async clearLogs() {
    this.logs = [];
    try {
      await AsyncStorage.removeItem(this.storageKey);
    } catch (error) {
      log.error('Failed to clear logs:', error);
    }
  }

  /**
   * Get all logs
   */
  getLogs(level?: LogLevel): LogEntry[] {
    // Note: This is synchronous, so we can't await ensureLogsLoaded
    // Logs will be loaded on first write operation
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  /**
   * Get recent logs
   */
  getRecentLogs(count: number = 100): LogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Export logs as JSON
   */
  async exportLogs(): Promise<void> {
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        platform: Platform.OS,
        version: Platform.Version,
        totalLogs: this.logs.length,
        logs: this.logs,
      };

      const jsonData = JSON.stringify(exportData, null, 2);
      const filename = `app-logs-${Date.now()}.json`;
      
      if (Platform.OS === 'web') {
        // Web export
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Mobile export
        const fileUri = `${FileSystem.documentDirectory}${filename}`;
        await FileSystem.writeAsStringAsync(fileUri, jsonData, {
          encoding: FileSystem.EncodingType.UTF8,
        });

        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(fileUri, {
            mimeType: 'application/json',
            dialogTitle: 'Export Logs',
          });
        }
      }
    } catch (error) {
      log.error('Failed to export logs:', error);
      throw error;
    }
  }

  /**
   * Log app lifecycle events
   */
  logAppEvent(event: string, data?: any) {
    this.info(`App Event: ${event}`, data);
  }

  /**
   * Log API calls
   */
  logApiCall(method: string, url: string, data?: any) {
    this.debug(`API Call: ${method} ${url}`, data);
  }

  /**
   * Log API errors
   */
  logApiError(method: string, url: string, error: any) {
    this.error(`API Error: ${method} ${url}`, error);
  }

  /**
   * Log navigation events
   */
  logNavigation(from: string, to: string, params?: any) {
    this.debug(`Navigation: ${from} → ${to}`, params);
  }

  /**
   * Log user actions
   */
  logUserAction(action: string, data?: any) {
    this.info(`User Action: ${action}`, data);
  }

  /**
   * Get log statistics
   */
  getLogStats() {
    const stats = {
      total: this.logs.length,
      debug: 0,
      info: 0,
      warn: 0,
      error: 0,
      oldestLog: this.logs[0]?.timestamp,
      newestLog: this.logs[this.logs.length - 1]?.timestamp,
    };

    this.logs.forEach(log => {
      stats[log.level]++;
    });

    return stats;
  }
}

// Export singleton instance
const loggingServiceInstance = LoggingService.getInstance();

// Named export for consistency
export const loggingService = loggingServiceInstance;

// Default export for backward compatibility
export default loggingServiceInstance;